import { useMemo, useState } from 'react';
import type { CourseHole, GroupRoundEnriched } from '@/lib/db/types';
import type { GameStakeConfig, RoundPlayer, WolfHoleState, WolfRoundConfig } from '@/lib/games/engine/types';
import {
  computeWolfTotals,
  mergeWolfConfig,
  wolfPlayerIndex,
  type WolfHoleLedgerEntry,
} from '@/lib/games/formats/wolf';
import { appliesMoney, getMoneyAmount } from '@/lib/games/engine/stakes-helpers';
import { balanceNetOwed } from '@/lib/games/formats/balance';

interface Props {
  round: GroupRoundEnriched;
  players: RoundPlayer[];
  holes: CourseHole[];
  scoresByKey: Record<string, (number | null)[]>;
  stake: GameStakeConfig;
  currentHole: number;
}

function modeLabel(entry: WolfHoleLedgerEntry, names: string[]): string {
  if (entry.mode === 'blind') return '🐺 Blind';
  if (entry.mode === 'lone') return '🐺 Lone';
  if (entry.mode === 'partner' && entry.partnerIndex != null) {
    return `🤝 ${names[entry.partnerIndex] ?? 'Partner'}`;
  }
  return '—';
}

export function WolfLiveBoard({ round, players, holes, scoresByKey, stake, currentHole }: Props) {
  const [historyOpen, setHistoryOpen] = useState(false);

  const game = round.games?.find((g) => g.enabled && (g.format_id === 'wolf' || g.format_id === 'lone-wolf'));
  const config = (game?.config || {}) as Record<string, unknown>;
  const holeState = (config.holeState || {}) as Record<number, WolfHoleState>;
  const wolfConfig = mergeWolfConfig(config.wolfConfig as WolfRoundConfig | undefined);

  const { totals, log } = useMemo(
    () => computeWolfTotals(players, scoresByKey, holes, holeState, wolfConfig),
    [players, scoresByKey, holes, holeState, wolfConfig],
  );

  const names = players.map((p) => p.name);
  const keys = players.map((p) => p.playerKey);
  const holeIx = Math.max(0, Math.min(17, currentHole));
  const wolfK = wolfPlayerIndex(holeIx);
  const currentEntry = log.find((e) => e.hole === holeIx + 1);

  const pointVals = keys.map((k) => totals[k] || 0);
  const dollarsPerPoint = stake.moneyPerPoint ?? getMoneyAmount(stake);
  const netCents = appliesMoney(stake)
    ? balanceNetOwed(keys, pointVals, dollarsPerPoint)
    : {};

  const ranked = keys
    .map((k, i) => ({ k, name: names[i], pts: pointVals[i], cents: netCents[k] ?? 0 }))
    .sort((a, b) => b.pts - a.pts);

  return (
    <div className="rounded-xl border border-gold/40 bg-navy px-3.5 py-3 text-cream">
      <div className="mb-2 text-[11px] font-extrabold tracking-wide text-gold-bright">WOLF LEADERBOARD</div>

      <div className="mb-3 grid grid-cols-2 gap-2 text-[10px]">
        <div>
          <span className="text-cream/60">Hole</span>
          <div className="font-bold">{holeIx + 1}</div>
        </div>
        <div>
          <span className="text-cream/60">Wolf</span>
          <div className="font-bold">{names[wolfK] ?? '—'}</div>
        </div>
        {currentEntry && (
          <>
            <div>
              <span className="text-cream/60">Mode</span>
              <div className="font-bold">{modeLabel(currentEntry, names)}</div>
            </div>
            <div>
              <span className="text-cream/60">Hole result</span>
              <div className="font-bold text-[10px] leading-tight">{currentEntry.summary}</div>
            </div>
          </>
        )}
      </div>

      <div className="mb-2 text-[9px] font-bold uppercase tracking-wide text-gold-bright/80">Running totals</div>
      <div className="space-y-1">
        {ranked.map((r, i) => (
          <div key={r.k} className="flex justify-between text-xs">
            <span className={i === 0 ? 'font-bold text-gold' : 'text-cream/90'}>
              {i + 1}. {r.name}
            </span>
            <span className="font-mono">
              {r.pts} pt{r.pts === 1 ? '' : 's'}
              {appliesMoney(stake) && (
                <span className={r.cents >= 0 ? ' text-green-300' : ' text-red-300'}>
                  {' '}({r.cents >= 0 ? '+' : '−'}${(Math.abs(r.cents) / 100).toFixed(0)})
                </span>
              )}
            </span>
          </div>
        ))}
      </div>

      {log.length > 0 && (
        <div className="mt-3 border-t border-cream/15 pt-2">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="text-[10px] font-bold text-gold-bright"
          >
            {historyOpen ? '▼' : '▶'} Hole history ({log.length})
          </button>
          {historyOpen && (
            <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
              {[...log].reverse().map((e) => (
                <div key={e.hole} className="rounded bg-cream/10 px-2 py-1 text-[10px]">
                  <span className="font-bold">#{e.hole}</span> · {names[e.wolfIndex]} · {modeLabel(e, names)}
                  <div className="text-cream/70">{e.summary}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
